import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Activity, 
  CheckCircle2, 
  RefreshCw, 
  ArrowRight, 
  Info, 
  ChevronRight, 
  Dna,
  Scale,
  Zap
} from 'lucide-react';
import { Layout } from '@/components/Layout';
import { DiagnosisForm } from '@/components/Forms';
import { useDiagnosis } from '@/hooks/useDiagnosis';
import { 
  ROUTE_PATHS, 
  CONSTITUTION_TYPES, 
  type DiagnosisResult, 
  formatPrice 
} from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

/**
 * 체질 진단 페이지
 * 사상체질 이론을 기반으로 사용자의 신체 특성을 분석하고 맞춤형 단백환을 추천합니다.
 */
const Diagnosis: React.FC = () => {
  const navigate = useNavigate();
  const { 
    isCompleted, 
    result, 
    reset 
  } = useDiagnosis();

  // 진단 완료 시 결과 데이터를 저장하거나 처리하는 핸들러
  const handleComplete = (diagnosisResult: DiagnosisResult) => {
    // 로직은 useDiagnosis 내부에서 처리되므로 여기서는 추가 UI 반응만 담당할 수 있음
    console.log('Diagnosis Completed:', diagnosisResult);
  };

  return (
    <Layout>
      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/4 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 translate-y-1/4 -translate-x-1/4 w-[400px] h-[400px] bg-primary/10 rounded-full blur-3xl pointer-events-none" />

        <div className="container mx-auto px-4 py-12 relative z-10">
          <AnimatePresence mode="wait">
            {!isCompleted ? (
              <motion.div
                key="diagnosis-form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="max-w-3xl mx-auto"
              >
                <div className="text-center mb-10">
                  <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary bg-primary/5">
                    AI 사상체질 분석 시스템
                  </Badge>
                  <h1 className="text-4xl font-bold mb-4 tracking-tight">
                    당신만의 <span className="text-primary">체질 코드</span>를 찾아보세요
                  </h1>
                  <p className="text-muted-foreground text-lg max-w-xl mx-auto">
                    장부의 허실과 기의 방향성을 분석하여 최적의 단백질 섭취 솔루션을 제안합니다.
                  </p>
                </div>

                <DiagnosisForm onComplete={handleComplete} />

                <div className="mt-12 p-6 bg-secondary/30 rounded-2xl border border-border flex items-start gap-4">
                  <Info className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                  <div className="text-sm text-muted-foreground leading-relaxed">
                    <p className="font-semibold text-foreground mb-1">유의사항</p>
                    본 시스템은 사상체질 이론을 기반으로 한 영양 섭취 가이드라인을 제공하는 비의료적 지원 도구입니다. 
                    의학적 진단이나 치료를 목적으로 하지 않으며, 정확한 진료는 가까운 한의원을 방문하시기 바랍니다.
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="diagnosis-result"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="max-w-5xl mx-auto space-y-8"
              >
                {/* Result Header */}
                <section className="bg-card border border-border rounded-3xl overflow-hidden shadow-xl">
                  <div className="grid grid-cols-1 md:grid-cols-2">
                    <div className="p-8 md:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-2 text-primary font-mono text-sm mb-4">
                        <CheckCircle2 className="w-4 h-4" />
                        ANALYSIS COMPLETE
                      </div>
                      <h2 className="text-3xl font-bold mb-2">분석 결과, 당신은</h2>
                      <div className="text-5xl font-bold text-primary mb-6">
                        {result?.constitution} <span className="text-2xl text-foreground/60 font-normal">타입입니다.</span>
                      </div>
                      <p className="text-lg text-muted-foreground mb-8">
                        {result ? CONSTITUTION_TYPES[result.constitution].description : ''}
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {result && CONSTITUTION_TYPES[result.constitution].traits.map((trait, idx) => (
                          <Badge key={idx} variant="secondary" className="px-4 py-2 text-sm bg-secondary">
                            # {trait}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="relative h-[300px] md:h-auto bg-muted">
                      <img 
                        src={IMAGES.BODY_ANALYSIS_1} 
                        alt="Body Analysis" 
                        className="absolute inset-0 w-full h-full object-cover mix-blend-overlay opacity-60"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-48 h-48 rounded-full border-4 border-primary/20 flex items-center justify-center animate-pulse">
                          <div className="w-32 h-32 rounded-full border-2 border-primary/40 flex items-center justify-center">
                            <span className="text-6xl font-bold text-primary">
                              {result?.recommendedProductType}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Detailed Analysis Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <Card className="border-none shadow-md bg-card/50 backdrop-blur">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                        <Dna className="w-4 h-4" /> 장부 허실 균형
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-end">
                          <span className="text-xs text-muted-foreground">강한 장부</span>
                          <span className="text-xl font-bold text-primary">
                            {result ? CONSTITUTION_TYPES[result.constitution].organBalance.strong : ''}
                          </span>
                        </div>
                        <div className="h-1 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full bg-primary w-[80%]" />
                        </div>
                        <div className="flex justify-between items-end">
                          <span className="text-xs text-muted-foreground">약한 장부</span>
                          <span className="text-xl font-bold text-destructive">
                            {result ? CONSTITUTION_TYPES[result.constitution].organBalance.weak : ''}
                          </span>
                        </div>
                        <div className="h-1 bg-secondary rounded-full overflow-hidden">
                          <div className="h-full bg-destructive/40 w-[30%]" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-md bg-card/50 backdrop-blur">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                        <Zap className="w-4 h-4" /> 기(氣)의 방향성
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center justify-center py-6">
                      <div className="text-2xl font-bold text-foreground mb-1">
                        {result ? CONSTITUTION_TYPES[result.constitution].direction : ''}
                      </div>
                      <p className="text-xs text-center text-muted-foreground">
                        당신의 체내 에너지는 주로 이 방향으로 흐르며,
                        이에 맞는 영양 섭취가 필요합니다.
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-md bg-card/50 backdrop-blur">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
                        <Scale className="w-4 h-4" /> 추천 제품 타입
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex flex-col items-center justify-center py-6">
                      <div className="text-5xl font-black text-primary mb-2 font-mono">
                        {result?.recommendedProductType}
                      </div>
                      <Badge variant="outline" className="font-mono">PREMIUM BLEND</Badge>
                    </CardContent>
                  </Card>
                </div>

                {/* Call to Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-8">
                  <Button 
                    size="lg" 
                    className="h-14 px-8 text-lg rounded-full group"
                    onClick={() => navigate(ROUTE_PATHS.PRODUCTS)}
                  >
                    맞춤형 단백환 보러가기
                    <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="h-14 px-8 text-lg rounded-full"
                    onClick={() => navigate(ROUTE_PATHS.MANAGEMENT)}
                  >
                    섭취 관리 시작하기
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="lg" 
                    className="h-14 px-8 text-lg rounded-full text-muted-foreground"
                    onClick={reset}
                  >
                    <RefreshCw className="mr-2 w-4 h-4" /> 다시 진단하기
                  </Button>
                </div>

                {/* Clinic Link B2B */}
                <div className="mt-16 p-8 rounded-3xl bg-primary/5 border border-primary/10 text-center">
                  <h3 className="text-xl font-bold mb-3">더 정밀한 관리가 필요하신가요?</h3>
                  <p className="text-muted-foreground mb-6">
                    근처 연계 한의원을 방문하시면 전문 한의사의 진료와 함께 <br className="hidden md:block" />
                    본 시스템을 통한 일상적인 체질 관리를 더욱 효과적으로 받으실 수 있습니다.
                  </p>
                  <Button variant="link" className="text-primary font-semibold">
                    가까운 연계 한의원 찾기 <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </Layout>
  );
};

export default Diagnosis;
